/**
 * Prefetch order: קודם נתונים (מוצרים + קטגוריות) → שמירה → ניווט מיידי.
 * כל התמונות נטענות ברקע אחרי הניווט (ללא הגבלה) – המשתמש מתחיל לעבוד מהר.
 */

import { base44 } from "@/api/base44Client";
import {
  savePrefetched,
  getProxyImageUrl,
  PREFETCH_IMAGE_CACHE,
  isImageCacheAvailable,
} from "./prefetchStorage";

const PAGE_SIZE = 1000;
const IMAGE_CONCURRENCY = 150; // תמונות במקביל – טעינה מהירה של כל התמונות ברקע

function sortProductsByCompanyOrderInPlace(productsByCompany) {
  for (const key of Object.keys(productsByCompany)) {
    productsByCompany[key].sort((a, b) => {
      const oa = Number(a.order_in_company ?? a.orderInCompany ?? 0);
      const ob = Number(b.order_in_company ?? b.orderInCompany ?? 0);
      if (oa !== ob) return oa - ob;
      return (a.name || "").localeCompare(b.name || "", "he");
    });
  }
}

/**
 * מחזיר Blob של תמונה: קודם מ־Cache API (אם כבר נשמר מדף מוצרים/קטגוריות), אחרת מושך מהרשת ושומר ב־cache.
 * @param {string} url - כתובת התמונה (proxy URL)
 * @returns {Promise<Blob|null>}
 */
async function getImageBlobFromCacheOrFetch(url) {
  if (isImageCacheAvailable()) {
    try {
      const cache = await caches.open(PREFETCH_IMAGE_CACHE);
      const cached = await cache.match(url);
      if (cached) {
        return await cached.blob();
      }
    } catch (_) {}
  }
  const res = await fetch(url);
  if (!res.ok) return null;
  const blob = await res.blob();
  if (isImageCacheAvailable()) {
    try {
      const cache = await caches.open(PREFETCH_IMAGE_CACHE);
      await cache.put(url, new Response(blob, { headers: res.headers }));
    } catch (_) {}
  }
  return blob;
}

/**
 * שלב 1: שואב את כל המוצרים (כל העמודים), קטגוריות ותת־קטגוריות, שומר במכשיר.
 * ללא טעינת תמונות – מסתיים מהר (~10–20 שניות) ואז אפשר לנווט.
 *
 * @param {string} storeId
 * @param {(state: { phase: string, progress: number, message: string, error?: string }) => void} onProgress
 * @returns {Promise<void>}
 */
export async function runPrefetchOrder(storeId, onProgress) {
  if (!storeId) {
    onProgress({ phase: "done", progress: 0, message: "", error: "חסר מזהה חנות" });
    return;
  }

  try {
    onProgress({ phase: "products", progress: 0, message: "מוריד מוצרים…" });
    const allProducts = [];
    let page = 1;
    let pages = 1;
    let res;
    do {
      res = await base44.entities.Product.listForMyStore({
        page,
        limit: PAGE_SIZE,
      });
      const data = res?.data || [];
      const pagination = res?.pagination || {};
      allProducts.push(...data);
      pages = Math.max(1, pagination.pages || 1);
      const pct = Math.min(45, Math.round((page / pages) * 45));
      const totalSoFar = allProducts.length.toLocaleString("he-IL");
      const pageInfo = pages > 1 ? ` (עמוד ${page} מתוך ${pages})` : "";
      onProgress({
        phase: "products",
        progress: pct,
        message: `מוריד מוצרים… ${totalSoFar}${pageInfo}`,
      });
      if (page >= pages) break;
      page += 1;
    } while (true);

    const storeInfo = res?.store || null;

    onProgress({ phase: "categories", progress: 50, message: "טוען קטגוריות וספקים…" });
    const [categories, subcategories, subsubcategories, companies] = await Promise.all([
      base44.entities.Category.list(),
      base44.entities.SubCategory.list(),
      base44.entities.SubSubCategory.list(),
      base44.entities.Company.list(),
    ]);
    const catList = Array.isArray(categories) ? categories : [];
    const subList = Array.isArray(subcategories) ? subcategories : [];
    const subSubList = Array.isArray(subsubcategories) ? subsubcategories : [];
    const companiesList = Array.isArray(companies) ? companies : (companies?.data || []);

    // מוצרים לפי חברה – רק ממגוון הסניף (allProducts כבר מ-listForMyStore)
    const productsByCompany = {};
    for (const p of allProducts) {
      const cid = p.company_id?._id ?? p.company_id;
      if (!cid) continue;
      const key = String(cid);
      if (!productsByCompany[key]) productsByCompany[key] = [];
      productsByCompany[key].push(p);
    }
    sortProductsByCompanyOrderInPlace(productsByCompany);

    onProgress({ phase: "images", progress: 60, message: "מוריד לוגואים…" });
    const MAX_LOGO_BASE64 = 80 * 1024; // ~80KB per logo so localStorage doesn't explode
    const companiesWithLogos = await Promise.all(
      (companiesList || []).map(async (c) => {
        if (!c.logo_url) return { ...c };
        const proxyUrl = getProxyImageUrl(c.logo_url);
        if (!proxyUrl) return { ...c };
        try {
          const blob = await getImageBlobFromCacheOrFetch(proxyUrl);
          if (!blob || blob.size > MAX_LOGO_BASE64) return { ...c };
          const dataUrl = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          return { ...c, logo_data_url: dataUrl };
        } catch {
          return { ...c };
        }
      })
    );

    let chainWithLogo = null;
    const chainId = storeInfo?.chain_id?._id ?? storeInfo?.chain_id;
    if (chainId) {
      try {
        const chains = await base44.entities.Chain.list();
        const chain = Array.isArray(chains) ? chains.find((c) => String(c._id || c.id) === String(chainId)) : null;
        if (chain?.logo_url) {
          const proxyUrl = getProxyImageUrl(chain.logo_url);
          if (proxyUrl) {
            try {
              const blob = await getImageBlobFromCacheOrFetch(proxyUrl);
              if (blob && blob.size <= MAX_LOGO_BASE64) {
                const dataUrl = await new Promise((resolve, reject) => {
                  const r = new FileReader();
                  r.onload = () => resolve(r.result);
                  r.onerror = reject;
                  r.readAsDataURL(blob);
                });
                chainWithLogo = { ...chain, logo_data_url: dataUrl };
              }
            } catch {
              // ignore, fallback without logo_data_url
            }
          }
          if (!chainWithLogo) chainWithLogo = { ...chain };
        } else if (chain) {
          chainWithLogo = { ...chain };
        }
      } catch (_) {}
    }

    onProgress({ phase: "categories", progress: 85, message: "שומר במכשיר…" });
    await savePrefetched(storeId, {
      products: allProducts,
      categories: catList,
      subcategories: subList,
      subsubcategories: subSubList,
      store: storeInfo,
      companies: companiesWithLogos,
      productsByCompany,
      chain: chainWithLogo,
    });

    onProgress({ phase: "done", progress: 100, message: "מוכן – מעבר לדף המוצרים" });
  } catch (err) {
    const message = err?.message || "שגיאה לא צפויה";
    onProgress({
      phase: "done",
      progress: 0,
      message: "",
      error: message,
    });
    throw err;
  }
}

/**
 * שלב 2 (רקע): טוען את כל תמונות המוצרים לשמירה במטמון.
 * קוראים אחרי הניווט – רץ ברקע, בלי להחזיק את המשתמש.
 * אין הגבלה על מספר תמונות – כל המוצרים שנשמרו מקבלים תמונה.
 * לא רץ כשאין רשת – מונע מאות שגיאות בקונסול.
 *
 * @param {string} storeId
 */
function isSlowOrDataSaverConnection() {
  if (typeof navigator === "undefined") return false;
  if (!navigator.onLine) return true;
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn?.saveData) return true;
  const type = (conn?.effectiveType || "").toLowerCase();
  if (type === "2g" || type === "3g") return true;
  return false;
}

/** לא מריצים טעינת תמונות ברקע – חוסך אלפי בקשות ב־3G ומאפשר למערכת לרוץ מהלוקאל. */
export async function runBackgroundImagePrefetch(_storeId) {
  return;
}

const LIGHT_PAGE_SIZE = 1000;

/** מונע שתי הורדות מקבילות של אותו מגוון (שעה + כניסה לקטגוריה וכו') */
let lightPrefetchInFlight = null;

/**
 * עדכון מגוון "קל" ברקע – רק מוצרים, קטגוריות, ספקים (בלי תמונות ולוגואים).
 * מתאים לרענון אוטומטי כשעברה שעה מאז "התחל הזמנה".
 * @param {string} storeId
 * @returns {Promise<void>}
 */
export async function runLightPrefetch(storeId) {
  if (!storeId) return;
  if (lightPrefetchInFlight) return lightPrefetchInFlight;

  const run = async () => {

  const allProducts = [];
  let page = 1;
  let pages = 1;
  let res;
  do {
    res = await base44.entities.Product.listForMyStore({
      page,
      limit: LIGHT_PAGE_SIZE,
    });
    const data = res?.data || [];
    const pagination = res?.pagination || {};
    allProducts.push(...data);
    pages = Math.max(1, pagination.pages || 1);
    if (page >= pages) break;
    page += 1;
  } while (true);

  const storeInfo = res?.store || null;

  const [categories, subcategories, subsubcategories, companies] = await Promise.all([
    base44.entities.Category.list(),
    base44.entities.SubCategory.list(),
    base44.entities.SubSubCategory.list(),
    base44.entities.Company.list(),
  ]);
  const catList = Array.isArray(categories) ? categories : [];
  const subList = Array.isArray(subcategories) ? subcategories : [];
  const subSubList = Array.isArray(subsubcategories) ? subsubcategories : [];
  const companiesList = Array.isArray(companies) ? companies : (companies?.data || []);

  const productsByCompany = {};
  for (const p of allProducts) {
    const cid = p.company_id?._id ?? p.company_id;
    if (!cid) continue;
    const key = String(cid);
    if (!productsByCompany[key]) productsByCompany[key] = [];
    productsByCompany[key].push(p);
  }
  sortProductsByCompanyOrderInPlace(productsByCompany);

  let chain = null;
  const chainId = storeInfo?.chain_id?._id ?? storeInfo?.chain_id;
  if (chainId) {
    try {
      const chains = await base44.entities.Chain.list();
      chain = Array.isArray(chains) ? chains.find((c) => String(c._id || c.id) === String(chainId)) : null;
    } catch (_) {}
  }

    await savePrefetched(storeId, {
      products: allProducts,
      categories: catList,
      subcategories: subList,
      subsubcategories: subSubList,
      store: storeInfo,
      companies: companiesList,
      productsByCompany,
      chain: chain || null,
    });
  };

  lightPrefetchInFlight = run().finally(() => {
    lightPrefetchInFlight = null;
  });
  return lightPrefetchInFlight;
}
